import { __assign } from "tslib";
import { calc, listen, pointer, spring, styler, value } from 'popmotion';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { throttle } from 'throttle-debounce';
import { Lethargy } from './util/lethargy';
export var useCarousel = function (options) {
    var _a = useState(0), gen = _a[1];
    var forceUpdate = useCallback(function () { return gen(function (x) { return x + 1; }); }, [gen]);
    var _b = useState(0), current = _b[0], setCurrent = _b[1];
    var _c = useState(0), total = _c[0], setTotal = _c[1];
    var target = useRef(0);
    var isActiveRef = useRef(null);
    var isActive = function (childIndex) {
        if (isActiveRef.current)
            return isActiveRef.current(current, childIndex) === 'active';
        return false;
    };
    var getPosition = function (childIndex) {
        if (isActiveRef.current)
            return isActiveRef.current(current, childIndex);
    };
    var setGetPosition = function (update) {
        return (isActiveRef.current = update());
    };
    var has = function (index) { return index >= 0 && index < total; };
    var hasNext = function () { return has(current + 1); };
    var hasPrevious = function () { return has(current - 1); };
    var goTo = function (index) {
        if (has(index))
            setCurrent(index);
    };
    var goNext = function () { return goTo(current + 1); };
    var goPrevious = function () { return goTo(current - 1); };
    return __assign({ current: current, setCurrent: function (current) {
            setCurrent(current);
            forceUpdate();
        }, target: target,
        total: total,
        setTotal: setTotal,
        has: has,
        hasNext: hasNext,
        hasPrevious: hasPrevious,
        goTo: goTo,
        goNext: goNext,
        goPrevious: goPrevious,
        isActive: isActive,
        getPosition: getPosition,
        setGetPosition: setGetPosition }, options);
};
var mix = calc.getValueFromProgress;
var MY_RETINA_FIXING_MAGIC_NUMBER = 3;
var calcSnaps = function (dom) {
    if (!dom)
        return null;
    var pageWidth = dom.offsetWidth;
    var children = Array.from(dom.firstChild.children);
    return children.reduce(function (_a, child, i) {
        var pages = _a.pages, elements = _a.elements;
        var prevPage = pages[pages.length - 1];
        var prevElement = elements[elements.length - 1];
        var width = child.getBoundingClientRect().width;
        var offset = prevElement + width;
        elements.push(offset);
        if (prevElement > 0 &&
            offset - prevPage > pageWidth + MY_RETINA_FIXING_MAGIC_NUMBER)
            pages.push(prevElement);
        return { pages: pages, elements: elements };
    }, { pages: [0], elements: [0] });
};
var closest = function (snaps, value) {
    return snaps.reduce(function (prev, curr) {
        return Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev;
    });
};
var angleIsVertical = function (angle) {
    var isUp = angle <= -90 + 45 && angle >= -90 - 45;
    var isDown = angle <= 90 + 45 && angle >= 90 - 45;
    return isUp || isDown;
};
var snapToAnimation = function (options) {
    return spring(__assign(__assign({}, options), { stiffness: 100, damping: 20 }));
};
export var Carousel = function (_a) {
    var className = _a.className, children = _a.children, current = _a.current, setCurrent = _a.setCurrent, total = _a.total, setTotal = _a.setTotal, setGetPosition = _a.setGetPosition, _b = _a.snapTo, snapTo = _b === void 0 ? 'pages' : _b, _c = _a.tug, tug = _c === void 0 ? 0.4 : _c, _d = _a.power, power = _d === void 0 ? 0.25 : _d, _e = _a.full, full = _e === void 0 ? false : _e;
    var dom = useRef(null);
    var offset = useMemo(function () { return value(0); }, []);
    var target = useRef(0);
    var snapsRef = useRef({ pages: [0], elements: [0] });
    var widthRef = useRef(0);
    var preventClick = useRef(false);
    var activePage = useRef(0);
    var options = { tug: tug, power: power, snapTo: snapTo };
    var optionsRef = useRef(options);
    var content = function () { return dom.current.firstChild; };
    var max = function () { return content().scrollWidth - dom.current.offsetWidth; };
    var snapKey = function (snaps) {
        return snaps.pages.map(Math.round).join('.') +
            '|' +
            snaps.elements.map(Math.round).join('.');
    };
    var update = useCallback(function (force) {
        var snaps = snapsRef.current;
        var newSnaps = calcSnaps(dom.current);
        if (!newSnaps)
            return;
        if (!force && snapKey(snaps) === snapKey(newSnaps))
            return;
        setTotal(Math.ceil(newSnaps.pages.length));
        snapsRef.current = newSnaps;
        var pages = newSnaps.pages;
        // jump to current page
        spring(pages[activePage.current]);
    }, []);
    var setActivePage = useCallback(function (destination) {
        var pages = snapsRef.current.pages;
        var newPage = destination === max()
            ? pages.length - 1
            : pages.indexOf(closest(pages, destination));
        activePage.current = newPage;
        setCurrent(newPage);
    }, []);
    var spring = useCallback(function (destination, animate) {
        if (animate === void 0) { animate = snapToAnimation; }
        if (!dom.current)
            return;
        var from = offset.get();
        var to = -Math.min(destination, max());
        target.current = -to;
        setActivePage(-to);
        if (from === to)
            return;
        animate({ from: from, to: to, velocity: offset.getVelocity() }).start(offset);
    }, []);
    var goTo = function (pageIndex) {
        var pages = snapsRef.current.pages;
        if (pageIndex < 0)
            return;
        if (pageIndex > total - 1)
            return;
        spring(pages[pageIndex]);
    };
    // on update
    useEffect(function () {
        var pageWidth = dom.current.offsetWidth;
        var pageChange = activePage.current !== current;
        widthRef.current = pageWidth;
        activePage.current = current;
        optionsRef.current = { tug: tug, power: power, snapTo: snapTo };
        update(pageChange);
    });
    // on create
    useEffect(function () {
        var contentStyler = styler(content());
        var clearSubscription = offset.subscribe(function (offset) {
            contentStyler.set('x', offset);
        }).unsubscribe;
        var snapToPoint = function (start) {
            var snapTo = optionsRef.current.snapTo;
            var snaps = snapsRef.current[snapTo];
            var from = offset.get();
            var velocity = offset.getVelocity();
            var distance = from - start;
            var amplitude = power * velocity;
            var idealTarget = Math.round(from + amplitude);
            var snap = closest(snaps, -idealTarget);
            preventClick.current = Math.abs(distance) > 1;
            spring(snap);
        };
        var clearTrack = function () { };
        var overDrag = function (v) {
            var from = -max();
            var to = 0;
            if (v < from)
                return mix(from, v, tug);
            if (v > to)
                return mix(to, v, tug);
            return v;
        };
        var clearMove = listen(dom.current, 'mousedown touchstart').start(function (event) {
            var start = offset.get();
            if (event instanceof MouseEvent && event.which !== 1)
                return;
            preventClick.current = false;
            clearTrack = pointer({
                x: start,
                preventDefault: false
            })
                .pipe(function (pos) { return pos.x; }, overDrag)
                .start(offset).stop;
            listen(document, 'mouseup touchend', { once: true }).start(function () {
                return snapToPoint(start);
            });
        }).stop;
        var onClick = function (e) {
            if (!preventClick.current)
                return clearTrack();
            e.stopPropagation();
            e.preventDefault();
        };
        var lethargy = new Lethargy();
        var wheelPanning = false;
        var onWheel = function (e) {
            var angle = calc.angle({
                x: e.deltaX,
                y: e.deltaY
            });
            var direction = lethargy.check(e.deltaX);
            if (angleIsVertical(angle))
                return;
            e.stopPropagation();
            e.preventDefault();
            if (direction === false)
                return;
            if (wheelPanning)
                return;
            goTo(current + direction);
            wheelPanning = true;
            setTimeout(function () { return (wheelPanning = false); }, 500);
        };
        dom.current.addEventListener('click', onClick, true);
        var clearClick = function () {
            if (dom.current)
                dom.current.removeEventListener('click', onClick, true);
        };
        dom.current.addEventListener('wheel', onWheel);
        var clearWheel = function () {
            if (dom.current)
                dom.current.removeEventListener('wheel', onWheel);
        };
        var onResize = throttle(250, function () { return update(true); });
        window.addEventListener('resize', onResize);
        var clearResize = function () { return window.removeEventListener('resize', onResize); };
        setGetPosition(function () { return function (gotoPage, childIndex) {
            var _a = snapsRef.current, pages = _a.pages, elements = _a.elements;
            if (activePage.current !== gotoPage)
                target.current = Math.min(pages[gotoPage], max());
            var pos = Math.round(elements[childIndex]);
            var next = Math.round(elements[childIndex + 1]);
            var page = target.current;
            var nextPage = target.current + dom.current.offsetWidth;
            var res = pos + MY_RETINA_FIXING_MAGIC_NUMBER < page
                ? 'left'
                : next - MY_RETINA_FIXING_MAGIC_NUMBER > nextPage
                    ? 'right'
                    : 'active';
            return res;
        }; });
        return function () {
            clearSubscription();
            clearMove();
            clearTrack();
            clearClick();
            clearWheel();
            onResize.cancel();
            clearResize();
        };
    }, []);
    return (React.createElement("div", { ref: dom, style: {
            overflow: full ? undefined : 'hidden',
            height: '100%',
            width: '100%',
            userSelect: 'none',
            touchAction: 'pan-y pinch-zoom'
        }, className: className && String(className) },
        React.createElement("div", { style: { height: '100%', display: 'flex' }, onDragStart: function (e) { return e.preventDefault(); } }, children)));
};
//# sourceMappingURL=carousel.js.map